// apps/backoffice/src/pages/Menu.tsx
// Управление меню бара и кухни: категории + позиции + отчёт продаж.

import { useState, type FormEvent } from 'react'
import { api } from '@/api/client'
import { useFetch, useLazyFetch, useMutate } from '@/hooks/useApi'
import { useConfigQuery } from '@/context/ClubContext'
import {
  AsyncState,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Icon,
  IconButton,
  ImageUpload,
  Input,
  Modal,
  PageHead,
  SectionHead,
  Select
} from '@/components/ui'
import { ClubGate } from '@/components/ClubGate'
import { money, count, dateInputToMs, msToDateInput } from '@/lib/format'
import { Cur } from '@/lib/currency'
import { deleteImage, uploadImage } from '@/lib/uploads'
import { RecipeEditor } from '@/components/RecipeEditor'

/**
 * Миниатюра позиции в списке меню. Пустая ячейка вместо картинки — это норма
 * (у большинства позиций её нет), поэтому рисуем спокойную заглушку, а битую
 * ссылку прячем по onError, чтобы в таблице не висел значок сломанного файла.
 */
function MenuItemThumb({ url }: { url: string | null }): React.JSX.Element {
  if (!url) return <span className="muted">—</span>
  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      onError={(e) => {
        e.currentTarget.style.visibility = 'hidden'
      }}
      style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, display: 'block' }}
    />
  )
}

const DAY_MS = 24 * 60 * 60 * 1000

// ─────────────── Типы контракта ───────────────

interface MenuCategory {
  id: string
  name: string
  section: 'bar' | 'kitchen'
  position: number
  isActive: boolean
  updatedAt: number
}

interface MenuItem {
  id: string
  categoryId: string
  name: string
  description: string | null
  /** Объём/порция: «0,5 л», «250 г». Терминал показывает в каталоге и клеит в чек. */
  portion: string | null
  price: number
  imageUrl: string | null
  position: number
  isActive: boolean
  updatedAt: number
}

interface BarOrderItemRow {
  menuItemId: string
  name: string
  qty: number
  revenue: number
}

interface BarSalesReport {
  from: number
  to: number
  byItem: BarOrderItemRow[]
  totals: { qty: number; revenue: number }
}

const SECTION_LABEL: Record<string, string> = {
  bar: 'Бар',
  kitchen: 'Кухня'
}

// ─────────────── Корень страницы ───────────────

export function Menu(): React.JSX.Element {
  return (
    <>
      <PageHead
        eyebrow="Конфигурация"
        title="Меню бара и кухни"
        lead="Категории и позиции меню, которые отображаются на терминале при открытии барной карточки стола. Ниже — отчёт по продажам бара за выбранный период."
      />
      <ClubGate>
        <CategoriesSection />
        <ItemsSection />
        <SalesSection />
      </ClubGate>
    </>
  )
}

// ─────────────────────────── Категории ───────────────────────────

function CategoriesSection(): React.JSX.Element {
  const query = useConfigQuery()
  const list = useFetch<MenuCategory[]>('/v1/bar/categories', query, [query?.clubId])
  const [editing, setEditing] = useState<MenuCategory | 'new' | null>(null)
  const [deleting, setDeleting] = useState<MenuCategory | null>(null)
  const del = useMutate()

  const rows = list.data ?? []

  const onDeleteConfirm = async (): Promise<void> => {
    if (!deleting) return
    const ok = await del.mutate(() => api.del(`/v1/bar/categories/${deleting.id}`, { query }))
    if (ok) {
      setDeleting(null)
      list.reload()
    }
  }

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <SectionHead title="Категории меню" meta={`${count(rows.length)} кат.`} />

      <div className="toolbar">
        <div className="toolbar__left">
          <span className="muted">Группируйте позиции по разделам меню.</span>
        </div>
        <div className="toolbar__right">
          <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
            {Icon.plus}
            Новая категория
          </Button>
        </div>
      </div>

      <AsyncState
        query={list}
        isEmpty={rows.length === 0}
        empty="Категорий нет. Создайте первую, чтобы добавлять позиции."
      />

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Название</th>
                <th>Раздел</th>
                <th className="num">Порядок</th>
                <th>Активна</th>
                <th style={{ width: 96 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((cat) => (
                <tr key={cat.id}>
                  <td>{cat.name}</td>
                  <td>
                    <span
                      className="tag"
                      style={{
                        background: cat.section === 'bar' ? '#1d6d5c22' : '#c9a84c22',
                        color: cat.section === 'bar' ? '#1d6d5c' : '#c9a84c',
                        padding: '2px 10px',
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 500
                      }}
                    >
                      {SECTION_LABEL[cat.section] ?? cat.section}
                    </span>
                  </td>
                  <td className="num">{cat.position}</td>
                  <td>
                    {cat.isActive ? (
                      <span className="status">
                        <span className="dot dot--on" />
                        да
                      </span>
                    ) : (
                      <span className="status">
                        <span className="dot dot--off" />
                        нет
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="row-actions">
                      <IconButton title="Изменить" onClick={() => setEditing(cat)}>
                        {Icon.edit}
                      </IconButton>
                      <IconButton danger title="Удалить" onClick={() => setDeleting(cat)}>
                        {Icon.trash}
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <CategoryForm
          category={editing === 'new' ? null : editing}
          query={query}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.reload()
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Удалить категорию"
          message={`Удалить категорию «${deleting.name}»? Все позиции этой категории также будут скрыты.`}
          busy={del.saving}
          error={del.error}
          onConfirm={() => void onDeleteConfirm()}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  )
}

function CategoryForm({
  category,
  query,
  onClose,
  onSaved
}: {
  category: MenuCategory | null
  query: ReturnType<typeof useConfigQuery>
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const isEdit = category !== null
  const [name, setName] = useState(category?.name ?? '')
  const [section, setSection] = useState<'bar' | 'kitchen'>(category?.section ?? 'bar')
  const [position, setPosition] = useState(category ? String(category.position) : '0')
  const [isActive, setIsActive] = useState(category?.isActive ?? true)
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return setValidation('Укажите название категории.')
    const posNum = Number(position)
    if (!Number.isInteger(posNum) || posNum < 0)
      return setValidation('Порядок должен быть целым неотрицательным числом.')
    setValidation(null)

    const body = { name: trimmed, section, position: posNum, isActive }
    const ok = await save.mutate(() =>
      isEdit
        ? api.patch(`/v1/bar/categories/${category.id}`, body, { query })
        : api.post('/v1/bar/categories', body, { query })
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title={isEdit ? 'Изменить категорию' : 'Новая категория'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </Button>
          <Button variant="primary" busy={save.saving} type="submit" form="cat-form">
            Сохранить
          </Button>
        </>
      }
    >
      <form id="cat-form" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-grid">
          <Field label="Название" htmlFor="cat-name" full>
            <Input
              id="cat-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="напр. Бар, Кухня, Снеки"
              autoFocus
            />
          </Field>

          <Field label="Раздел" htmlFor="cat-section">
            <Select
              id="cat-section"
              value={section}
              onChange={(e) => setSection(e.target.value as 'bar' | 'kitchen')}
            >
              <option value="bar">Бар</option>
              <option value="kitchen">Кухня</option>
            </Select>
          </Field>

          <Field label="Порядок (меньше = выше)" htmlFor="cat-pos">
            <Input
              id="cat-pos"
              type="number"
              min={0}
              step={1}
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="0"
            />
          </Field>

          <div className="field field--full">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              Активна (видна в терминале)
            </label>
          </div>
        </div>

        {validation && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {validation}
          </div>
        )}
        {save.error && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {save.error}
          </div>
        )}
      </form>
    </Modal>
  )
}

// ─────────────────────────── Позиции меню ───────────────────────────

function ItemsSection(): React.JSX.Element {
  const query = useConfigQuery()
  const categories = useFetch<MenuCategory[]>('/v1/bar/categories', query, [query?.clubId])
  const list = useFetch<MenuItem[]>('/v1/bar/items', query, [query?.clubId])
  const [filterCatId, setFilterCatId] = useState<string | 'all'>('all')
  const [editing, setEditing] = useState<MenuItem | 'new' | null>(null)
  const [deleting, setDeleting] = useState<MenuItem | null>(null)
  // Техкарта блюда (ресторанный режим): состав правится на карточке блюда.
  const [recipeFor, setRecipeFor] = useState<MenuItem | null>(null)
  const del = useMutate()

  const cats = categories.data ?? []
  const allRows = list.data ?? []
  const rows = filterCatId === 'all' ? allRows : allRows.filter((i) => i.categoryId === filterCatId)

  const catMap = new Map(cats.map((c) => [c.id, c]))

  const onDeleteConfirm = async (): Promise<void> => {
    if (!deleting) return
    const ok = await del.mutate(() => api.del(`/v1/bar/items/${deleting.id}`, { query }))
    if (ok) {
      setDeleting(null)
      list.reload()
    }
  }

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <SectionHead title="Позиции меню" meta={`${count(rows.length)} поз.`} />

      <div className="toolbar">
        <div className="toolbar__left">
          {/* Фильтр по категории */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              className={`btn btn--sm ${filterCatId === 'all' ? 'btn--primary' : 'btn--ghost'}`}
              onClick={() => setFilterCatId('all')}
            >
              Все
            </button>
            {cats.map((c) => (
              <button
                key={c.id}
                className={`btn btn--sm ${filterCatId === c.id ? 'btn--primary' : 'btn--ghost'}`}
                onClick={() => setFilterCatId(c.id)}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
        <div className="toolbar__right">
          <Button
            variant="primary"
            size="sm"
            disabled={cats.length === 0}
            title={cats.length === 0 ? 'Сначала создайте категорию' : ''}
            onClick={() => setEditing('new')}
          >
            {Icon.plus}
            Новая позиция
          </Button>
        </div>
      </div>

      <AsyncState
        query={list}
        isEmpty={rows.length === 0}
        empty="Позиций нет. Добавьте первую позицию меню."
      />

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th style={{ width: 52 }} />
                <th>Название</th>
                <th>Категория</th>
                <th>Объём</th>
                <th>Описание</th>
                <th className="num">Цена</th>
                <th className="num">Порядок</th>
                <th>Активна</th>
                <th style={{ width: 96 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => {
                const cat = catMap.get(item.categoryId)
                return (
                  <tr key={item.id}>
                    <td>
                      <MenuItemThumb url={item.imageUrl} />
                    </td>
                    <td>{item.name}</td>
                    <td>
                      {cat ? (
                        <span
                          className="tag"
                          style={{
                            background: cat.section === 'bar' ? '#1d6d5c22' : '#c9a84c22',
                            color: cat.section === 'bar' ? '#1d6d5c' : '#c9a84c',
                            padding: '2px 10px',
                            borderRadius: 20,
                            fontSize: 12,
                            fontWeight: 500
                          }}
                        >
                          {cat.name}
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      {item.portion ? (
                        <span className="mono" style={{ fontSize: 12 }}>
                          {item.portion}
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      {item.description ? (
                        <span className="muted" style={{ fontSize: 12 }}>
                          {item.description.length > 40
                            ? item.description.slice(0, 40) + '…'
                            : item.description}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="num">
                      {money(item.price)} <Cur />
                    </td>
                    <td className="num">{item.position}</td>
                    <td>
                      {item.isActive ? (
                        <span className="status">
                          <span className="dot dot--on" />
                          да
                        </span>
                      ) : (
                        <span className="status">
                          <span className="dot dot--off" />
                          нет
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="row-actions">
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Состав блюда и себестоимость"
                          onClick={() => setRecipeFor(item)}
                        >
                          Техкарта
                        </Button>
                        <IconButton title="Изменить" onClick={() => setEditing(item)}>
                          {Icon.edit}
                        </IconButton>
                        <IconButton danger title="Удалить" onClick={() => setDeleting(item)}>
                          {Icon.trash}
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {recipeFor && (
        <RecipeEditor
          menuItemId={recipeFor.id}
          menuItemName={recipeFor.name}
          menuItemPrice={recipeFor.price}
          onClose={() => setRecipeFor(null)}
          onSaved={() => setRecipeFor(null)}
        />
      )}

      {editing && (
        <ItemForm
          item={editing === 'new' ? null : editing}
          categories={cats}
          query={query}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.reload()
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Удалить позицию"
          message={`Удалить «${deleting.name}»? Действие нельзя отменить.`}
          busy={del.saving}
          error={del.error}
          onConfirm={() => void onDeleteConfirm()}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  )
}

function ItemForm({
  item,
  categories,
  query,
  onClose,
  onSaved
}: {
  item: MenuItem | null
  categories: MenuCategory[]
  query: ReturnType<typeof useConfigQuery>
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const isEdit = item !== null
  const [name, setName] = useState(item?.name ?? '')
  const [categoryId, setCategoryId] = useState(item?.categoryId ?? categories[0]?.id ?? '')
  const [price, setPrice] = useState(item ? String(item.price) : '')
  const [description, setDescription] = useState(item?.description ?? '')
  const [portion, setPortion] = useState(item?.portion ?? '')
  const [imageUrl, setImageUrl] = useState(item?.imageUrl ?? '')
  const [position, setPosition] = useState(item ? String(item.position) : '0')
  const [isActive, setIsActive] = useState(item?.isActive ?? true)
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return setValidation('Укажите название позиции.')
    if (!categoryId) return setValidation('Выберите категорию.')
    const priceNum = Number(price.replace(',', '.'))
    if (!Number.isFinite(priceNum) || priceNum < 0)
      return setValidation('Цена должна быть неотрицательным числом.')
    const posNum = Number(position)
    if (!Number.isInteger(posNum) || posNum < 0)
      return setValidation('Порядок должен быть целым неотрицательным числом.')
    setValidation(null)

    const body = {
      name: trimmed,
      categoryId,
      price: priceNum,
      description: description.trim() || null,
      portion: portion.trim() || null,
      imageUrl: imageUrl.trim() || null,
      position: posNum,
      isActive
    }

    const ok = await save.mutate(() =>
      isEdit
        ? api.patch(`/v1/bar/items/${item.id}`, body, { query })
        : api.post('/v1/bar/items', body, { query })
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title={isEdit ? 'Изменить позицию' : 'Новая позиция'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </Button>
          <Button variant="primary" busy={save.saving} type="submit" form="item-form">
            Сохранить
          </Button>
        </>
      }
    >
      <form id="item-form" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-grid">
          <Field label="Название" htmlFor="mi-name" full>
            <Input
              id="mi-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="напр. Пиво разливное"
              autoFocus
            />
          </Field>

          <Field label="Категория" htmlFor="mi-cat">
            <Select id="mi-cat" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({SECTION_LABEL[c.section] ?? c.section})
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label={
              <>
                Цена, <Cur />
              </>
            }
            htmlFor="mi-price"
          >
            <Input
              id="mi-price"
              type="number"
              min={0}
              step="any"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0.00"
            />
          </Field>

          <Field label="Объём / порция (необязательно)" htmlFor="mi-portion">
            <Input
              id="mi-portion"
              value={portion}
              onChange={(e) => setPortion(e.target.value)}
              placeholder="напр. 0,5 л"
              maxLength={24}
            />
          </Field>

          <Field label="Картинка (необязательно)" full>
            <ImageUpload
              value={imageUrl}
              onChange={setImageUrl}
              onPick={(file) => uploadImage(file, 'menu-item', query)}
              onRemove={(url) => deleteImage(url, query)}
              size={72}
              hint="Миниатюра в каталоге кассы. JPEG, PNG, GIF или WebP, до 5 МБ. Не забудьте сохранить."
            />
          </Field>

          <Field label="Описание (необязательно)" htmlFor="mi-desc" full>
            <Input
              id="mi-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="напр. светлое, нефильтрованное"
            />
          </Field>

          <Field label="Порядок (меньше = выше)" htmlFor="mi-pos">
            <Input
              id="mi-pos"
              type="number"
              min={0}
              step={1}
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="0"
            />
          </Field>

          <div className="field field--full">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              Активна (видна на терминале)
            </label>
          </div>
        </div>

        {validation && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {validation}
          </div>
        )}
        {save.error && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {save.error}
          </div>
        )}
      </form>
    </Modal>
  )
}

// ─────────────────────── Продажи бара за период ───────────────────────

function SalesSection(): React.JSX.Element {
  const query = useConfigQuery()
  const now = Date.now()
  const [fromStr, setFromStr] = useState(() => msToDateInput(now - 30 * DAY_MS))
  const [toStr, setToStr] = useState(() => msToDateInput(now))
  const report = useLazyFetch<BarSalesReport>()

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault()
    const from = dateInputToMs(fromStr)
    const to = dateInputToMs(toStr) + DAY_MS - 1
    void report.run('/v1/bar/orders/report', { from, to, ...(query ?? {}) })
  }

  const totals = report.data?.totals

  return (
    <div className="card" style={{ marginTop: 24 }}>
      <SectionHead title="Продажи бара за период" />

      <form className="report-form" onSubmit={onSubmit}>
        <Field label="С даты" htmlFor="bar-from">
          <Input
            id="bar-from"
            type="date"
            value={fromStr}
            max={toStr}
            onChange={(e) => setFromStr(e.target.value)}
            required
          />
        </Field>
        <Field label="По дату" htmlFor="bar-to">
          <Input
            id="bar-to"
            type="date"
            value={toStr}
            min={fromStr}
            onChange={(e) => setToStr(e.target.value)}
            required
          />
        </Field>
        <div className="field field--action">
          <Button variant="primary" type="submit" disabled={report.loading}>
            {report.loading ? <span className="spin" /> : null}
            Построить
          </Button>
        </div>
      </form>

      <div style={{ marginTop: 20 }}>
        <AsyncState query={report} loadingLabel="Считаем продажи бара…" />
        {report.data && totals && (
          <>
            <div className="kpi-grid">
              <div className="kpi">
                <div className="kpi__label">Выручка бара</div>
                <div className="kpi__value kpi__value--gold">
                  {money(totals.revenue)} <Cur />
                </div>
              </div>
              <div className="kpi">
                <div className="kpi__label">Позиций продано</div>
                <div className="kpi__value">{count(totals.qty)}</div>
              </div>
            </div>

            <div style={{ marginTop: 20 }}>
              <SectionHead title="По позициям" meta={`${count(report.data.byItem.length)} поз.`} />
              {report.data.byItem.length === 0 ? (
                <EmptyState message="Нет продаж за период." />
              ) : (
                <div className="table-wrap">
                  <table className="rtable">
                    <thead>
                      <tr>
                        <th>Позиция</th>
                        <th className="num">Кол-во</th>
                        <th className="num">Выручка</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.data.byItem.map((r) => (
                        <tr key={r.menuItemId}>
                          <td>{r.name}</td>
                          <td className="num">{count(r.qty)}</td>
                          <td className="num">
                            {money(r.revenue)} <Cur />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
